import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { deleteToken } from "@modules/token";
import { cancelTranscodeAllSync } from "@modules/upload";
import * as jobStatus from "@cores/jobStatus";
import { sessionKeeper } from "@cores/sessionKeeper";
import config from "@config/config";
import { JOB_ID_LCOAL_STORAGE_KEY } from "@constants";

import theme from "@styles/theme";
import { Tooltip, IconButton, Grid, Typography, Popover, Box, Divider, Stack } from "@mzc-pdc/ui";
import { IconOpenInNew } from "@mzc-cloudplex/icons";

import { Email, Name, ProfileItem, ToggleDivider, ToggleItem, ToggleList, RoundedButton } from "./profile-menu.styled";
import { UserProfile } from "@features/user-profile/user-profile";
import DialogCancelWarning from "@features/dialog/dialog-cancel-warning";
import { isLocal } from "@cores/enviromentExtension";
import LanguageMenu from "@features/language-menu/language-menu";
import useSettings from "@hooks/useSettings";
import { filterLocalesBySpaceVersion, getDefaultLanguage } from "@modules/localization";
import { OverflowTooltip } from "@components_v2/ellipsis-tooltip";

export const ProfileMenu = ({ headerTheme }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { i18nLng, updateLocaleLanguage } = useSettings({ type: "General" });

    const [modal, setModal] = useState("");
    const [warningMessage, setWarningMessage] = useState("");
    const [runningJobs, setRunningJobs] = useState([]);
    const [cachedLocales] = useState(() => JSON.parse(localStorage.getItem("i18nResources")));
    const [initLanguage] = useState(i18nLng ?? getDefaultLanguage());
    // 라벨은 게이트와 무관하게 전체 목록에서 찾는다 (현재 적용된 언어명이 그대로 보이도록)
    const [language] = useState(cachedLocales?.find((element) => element.value === initLanguage)?.label);

    const user = useSelector(({ user }) => user);
    const upload = useSelector(({ upload }) => upload);
    const spaceVersion = useSelector(({ stage }) => stage.version);

    // Space 버전에 따라 노출 언어를 게이팅한다. Space 전환 시 즉시 반영되도록 렌더 시점에 필터링한다. (DAM-463)
    const i18nResources = useMemo(
        () => filterLocalesBySpaceVersion(cachedLocales, spaceVersion),
        [cachedLocales, spaceVersion],
    );

    const [profileModalActive, setProfileModalActive] = useState(false);

    const anchorRef = useRef(null);

    const handleChangeLocale = useCallback(
        (lng) => {
            i18n.changeLanguage(lng).then(() => {
                updateLocaleLanguage(lng);
                window.location.reload();
            });
        },
        [i18n],
    );

    const openTermsPrivacy = useCallback(() => {
        const url = `https://cloudplex.megazone.com/${initLanguage !== "ko" ? `${initLanguage}/` : ""}privacy/latest`;
        window.open(url);
    }, []);

    const openTermsService = useCallback(() => {
        const url = `https://cloudplex.megazone.com/${initLanguage !== "ko" ? `${initLanguage}/` : ""}terms/latest`;
        window.open(url);
    }, []);

    const openCustomerService = useCallback(() => {
        window.open(`https://help.cloudplex.megazone.io/hc/ko`);
    }, []);

    const handleToggle = useCallback(() => {
        setProfileModalActive((prevOpen) => !prevOpen);
    }, []);

    const handleClose = useCallback((event) => {
        setProfileModalActive(false);
    }, []);

    const onHandleClickProfile = useCallback(() => {
        window.open(`https://myaccount.megazone.com/info`);
    }, []);
    const isPopsUser = user?.data?.issuer === "megazone.com";
    const logOutPops = (value) => {
        setModal("");
        sessionKeeper.reset();
        if (value) {
            const welcomePath = window.location.pathname.indexOf("/library") > -1 ? "/library/welcome" : "/welcome";
            const redirectUrl = `${window.location.origin}${welcomePath}`;
            window.location.href = `${config.mz_pops.oauth_host}/logout?next=${redirectUrl}`;
        }
        dispatch(deleteToken());
    };

    useEffect(() => {
        const jobs = (upload.jobs.data ?? []).filter(
            (v) => v.status === jobStatus.values.READY || v.status === jobStatus.values.INGESTING,
        );
        setRunningJobs(jobs);
    }, [upload.jobs.data]);

    const proceedLogout = useCallback(() => {
        const domain = sessionKeeper.getDomain();
        Cookies.remove("beforeLoginUrl", { domain });
        localStorage.removeItem(JOB_ID_LCOAL_STORAGE_KEY);
        if (isPopsUser) {
            setModal("logout");
            setWarningMessage(t(`common::msg::${"Would you like to log out from the account service as well?"}`));
        } else {
            sessionKeeper.reset();
            dispatch(deleteToken());
        }
    }, [isPopsUser]);

    const onHandleClickLogout = useCallback(() => {
        if (runningJobs?.length > 0) {
            setModal("cancelJobs");
            setWarningMessage(t(`common::msg::${"There is work in progress. Would you really go out?"}`));
            return;
        }
        proceedLogout();
    }, [runningJobs, proceedLogout]);

    return (
        <Grid>
            <Tooltip title={t(`common::label::Profile`, `Profile`)}>
                <IconButton size={"large"} circled ref={anchorRef} onClick={handleToggle}>
                    <UserProfile profileImageUrl={user?.data?.profileImageUrl} headerTheme={headerTheme} size={24} />
                </IconButton>
            </Tooltip>
            <Popover
                open={profileModalActive}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <ToggleList>
                    <ProfileItem>
                        <Typography variant={"caption"} color={theme.palette.greySecondary[600]}>
                            <strong>{t(`common::label::Account`, `Account`)}</strong>
                        </Typography>
                        <Grid container alignItems={`center`} sx={{ marginTop: `12px` }} flexWrap={"nowrap"}>
                            <Grid item sx={{ marginRight: `12px` }}>
                                <UserProfile profileImageUrl={user?.data?.profileImageUrl} size={44} />
                            </Grid>
                            {isPopsUser ? (
                                <Grid item sx={{ minWidth: 0 }}>
                                    {/* 이름·이메일은 말줄임 처리되므로 잘린 경우에만 툴팁으로 전체 값을 노출한다. */}
                                    <OverflowTooltip>
                                        <Name>{user?.data?.displayName}</Name>
                                    </OverflowTooltip>
                                    <OverflowTooltip>
                                        <Email>{user?.data?.username}</Email>
                                    </OverflowTooltip>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <Name sx={{ whiteSpace: `normal` }}>{user?.data?.id}</Name>
                                </Grid>
                            )}
                        </Grid>
                    </ProfileItem>
                    {isPopsUser && (
                        <>
                            <ToggleDivider />
                            <ToggleItem onClick={onHandleClickProfile}>
                                <strong>{t(`common::label::Manage Account`, `Manage Account`)}</strong>
                                <IconOpenInNew size={16} />
                            </ToggleItem>
                        </>
                    )}
                    <ToggleDivider />
                    <ToggleItem onClick={openCustomerService}>
                        <strong>{t(`common::label::Customer Service`)}</strong>
                        <IconOpenInNew size={16} />
                    </ToggleItem>
                    <ToggleDivider />
                    <ToggleItem onClick={onHandleClickLogout}>
                        {/*onClick={handleLogout}*/}
                        <strong> {t(`common::label::Log out`, `Log out`)}</strong>
                    </ToggleItem>
                    <ToggleDivider />
                    <Grid
                        container
                        justifyContent={"space-between"}
                        py={theme.spacing(0.75)}
                        px={theme.spacing(1)}
                        gap={theme.spacing(1)}
                    >
                        <Stack direction={"row"} gap={theme.spacing(1.5)}>
                            <RoundedButton
                                size={"xsmall"}
                                variant={"contained"}
                                color={"primary"}
                                onClick={openTermsPrivacy}
                            >
                                {t(`common::label::Privacy policy`)}
                            </RoundedButton>
                            <Divider orientation="vertical" flexItem />
                            <RoundedButton
                                size={"xsmall"}
                                variant={"contained"}
                                color={"greySecondary"}
                                onClick={openTermsService}
                            >
                                {t(`common::label::Terms & Conditions`)}
                            </RoundedButton>
                        </Stack>
                        <Box>
                            <LanguageMenu
                                locales={i18nResources}
                                locale={i18nLng}
                                label={language}
                                onClick={handleChangeLocale}
                            />
                        </Box>
                    </Grid>
                </ToggleList>
            </Popover>
            {modal === "cancelJobs" && (
                <DialogCancelWarning
                    onClose={(value) => {
                        setModal("");
                        if (value) {
                            dispatch(cancelTranscodeAllSync(runningJobs));
                            proceedLogout();
                        }
                    }}
                    message={warningMessage}
                />
            )}
            {modal === "logout" && (
                <DialogCancelWarning onClose={(value) => logOutPops(value)} message={warningMessage} />
            )}
        </Grid>
    );
};
export default ProfileMenu;
